import { useState, useMemo, useEffect, useCallback, Fragment } from "react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "@/i18n/translations";
import { useWorkstreamsByProjectQuery } from "@/hooks/queries/useWorkstreamsQuery";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import {
  WorkstreamStatus,
  AgentType,
  DOMAIN_AGENT_TYPES,
  AGENT_TYPE_LABELS,
  AGENT_TYPE_TRANSLATION_KEYS,
  AGENT_TYPE_ICONS,
  safeParseJsonArray,
} from "@/types/workstream";
import type { WorkstreamDto, WorkstreamFinding } from "@/types/workstream";
import { toast } from "sonner";

interface WorkstreamsTabProps {
  projectId: number;
}

type SortField = "status" | "riskScore";
type SortDirection = "asc" | "desc";

interface WorkstreamGroup {
  agentType: AgentType;
  items: WorkstreamDto[];
  redFlagsTotal: number;
  keyGapsTotal?: number;
}

// Checks whether a workstream's text content (summary, document type, key gaps,
// red flags, findings) contains the search query. Used to auto-expand and
// highlight matches, separately from matching on the agent label.
const workstreamContentMatches = (ws: WorkstreamDto, query: string): boolean => {
  if (ws.summary?.toLowerCase().includes(query)) return true;
  if (ws.documentType?.toLowerCase().includes(query)) return true;

  const keyGapsList = safeParseJsonArray<string>(ws.keyGaps);
  if (keyGapsList.some((gap) => gap.toLowerCase().includes(query))) return true;

  const redFlagsList = safeParseJsonArray<string>(ws.redFlags);
  if (redFlagsList.some((flag) => flag.toLowerCase().includes(query))) return true;

  const findings = safeParseJsonArray<WorkstreamFinding>(ws.findingsJson);
  return findings.some((finding) =>
    [finding.title, finding.description, finding.evidence, finding.recommendation]
      .some((field) => field?.toLowerCase().includes(query))
  );
};

// Splits text on case-insensitive matches of the query and wraps matches in <mark>.
const highlightMatches = (text: string, query: string): ReactNode => {
  if (!query) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const firstIndex = lowerText.indexOf(lowerQuery);
  if (firstIndex === -1) return text;

  const parts: ReactNode[] = [];
  let start = 0;
  let index = firstIndex;
  while (index !== -1) {
    if (index > start) parts.push(text.slice(start, index));
    parts.push(
      <mark key={index} className="bg-yellow-200 dark:bg-yellow-700/60 rounded px-0.5">
        {text.slice(index, index + query.length)}
      </mark>
    );
    start = index + query.length;
    index = lowerText.indexOf(lowerQuery, start);
  }
  if (start < text.length) parts.push(text.slice(start));
  return parts;
};

const WorkstreamsTab = ({ projectId }: WorkstreamsTabProps) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const { data: workstreams, isLoading, isError, error } = useWorkstreamsByProjectQuery(projectId);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>("status");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<AgentType>>(
    () => new Set(DOMAIN_AGENT_TYPES)
  );

  useEffect(() => {
    if (isError && error) {
      toast.error(t("noResults"));
    }
  }, [isError, error, t]);

  const getAgentLabel = useCallback((agentType: number): string => {
    const translationKey = AGENT_TYPE_TRANSLATION_KEYS[agentType];
    if (translationKey) {
      const translated = t(translationKey as Parameters<typeof t>[0]);
      if (translated !== translationKey) return translated;
    }
    return AGENT_TYPE_LABELS[agentType] ?? `Agent ${agentType}`;
  }, [t]);

  const getStatusBadge = (status: WorkstreamStatus) => {
    switch (status) {
      case WorkstreamStatus.Pending:
        return <Badge variant="secondary">{t("wsPending")}</Badge>;
      case WorkstreamStatus.Running:
        return <Badge variant="default">{t("wsRunning")}</Badge>;
      case WorkstreamStatus.Completed:
        return <Badge className="bg-green-500 hover:bg-green-600">{t("wsCompleted")}</Badge>;
      case WorkstreamStatus.Failed:
        return <Badge variant="destructive">{t("wsFailed")}</Badge>;
    }
  };

  const getRiskScoreDisplay = (riskScore: number | null) => {
    if (riskScore === null) return <span className="text-muted-foreground">—</span>;
    let colorClass = "text-green-600 dark:text-green-400";
    if (riskScore >= 7) colorClass = "text-red-600 dark:text-red-400";
    else if (riskScore >= 4) colorClass = "text-yellow-600 dark:text-yellow-400";
    return <span className={`font-semibold ${colorClass}`}>{riskScore}/10</span>;
  };

  const getConfidenceDisplay = (confidence: number | null) => {
    if (confidence === null) return <span className="text-muted-foreground">—</span>;
    return <span>{Math.round(confidence * 100)}%</span>;
  };

  const getSeverityBadge = (severity: string) => {
    const lower = severity.toLowerCase();
    if (lower === "critical" || lower === "high") {
      return <Badge variant="destructive">{severity}</Badge>;
    }
    if (lower === "medium") {
      return <Badge className="bg-yellow-500 hover:bg-yellow-600">{severity}</Badge>;
    }
    return <Badge variant="secondary">{severity}</Badge>;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="ml-1 text-muted-foreground" />;
    return sortDirection === "asc"
      ? <ArrowUp size={14} className="ml-1" />
      : <ArrowDown size={14} className="ml-1" />;
  };

  const filteredWorkstreams = useMemo(() => {
    if (!workstreams) return [];
    let result = [...workstreams];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((ws) => {
        const label = getAgentLabel(ws.agentType).toLowerCase();
        return label.includes(query) || workstreamContentMatches(ws, query);
      });
    }

    if (statusFilter !== "all") {
      const statusNum = Number(statusFilter);
      result = result.filter((ws) => ws.status === statusNum);
    }

    return result;
  }, [workstreams, searchQuery, statusFilter, getAgentLabel]);

  const groups = useMemo<WorkstreamGroup[]>(() => {
    const byAgentType = new Map<AgentType, WorkstreamDto[]>();
    for (const ws of filteredWorkstreams) {
      const list = byAgentType.get(ws.agentType) ?? [];
      list.push(ws);
      byAgentType.set(ws.agentType, list);
    }

    const comparator = (a: WorkstreamDto, b: WorkstreamDto) => {
      let comparison = 0;
      switch (sortField) {
        case "status":
          comparison = a.status - b.status;
          break;
        case "riskScore":
          comparison = (a.riskScore ?? -1) - (b.riskScore ?? -1);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    };

    return DOMAIN_AGENT_TYPES.filter((agentType) => byAgentType.has(agentType)).map((agentType) => {
      const items = [...(byAgentType.get(agentType) ?? [])].sort(comparator);
      const redFlagsTotal = items.reduce((sum, ws) => {
        const count = ws.redFlagsCount ?? safeParseJsonArray<string>(ws.redFlags).length;
        return sum + count;
      }, 0);
      const keyGapsTotal = items.reduce((sum, ws) => {
        const count = ws.keyGapsCount ?? safeParseJsonArray<string>(ws.keyGaps).length;
        return sum + count;
      }, 0);
      return { agentType, items, redFlagsTotal, keyGapsTotal };
    });
  }, [filteredWorkstreams, sortField, sortDirection]);

  const hasActiveFilters = Boolean(searchQuery) || statusFilter !== "all";
  const isSearching = Boolean(searchQuery);

  // Tracks groups the user has explicitly toggled while searching, so manual
  // clicks override the auto-expand-on-match behavior.
  const [manualOverrides, setManualOverrides] = useState<Set<AgentType>>(new Set());

  // Clearing the search box always resets everything back to fully collapsed.
  useEffect(() => {
    if (!isSearching) {
      setManualOverrides(new Set());
      setCollapsedGroups(new Set(DOMAIN_AGENT_TYPES));
    }
  }, [isSearching]);

  const isGroupExpanded = (agentType: AgentType) => {
    if (isSearching && !manualOverrides.has(agentType)) return true;
    return !collapsedGroups.has(agentType);
  };

  const toggleGroup = (agentType: AgentType) => {
    const currentlyExpanded = isGroupExpanded(agentType);
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (currentlyExpanded) {
        next.add(agentType);
      } else {
        next.delete(agentType);
      }
      return next;
    });
    if (isSearching) {
      setManualOverrides((prev) => new Set(prev).add(agentType));
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
  };

  const contentMatchIds = useMemo(() => {
    const ids = new Set<number>();
    if (!searchQuery) return ids;
    const query = searchQuery.toLowerCase();
    for (const ws of filteredWorkstreams) {
      if (workstreamContentMatches(ws, query)) ids.add(ws.id);
    }
    return ids;
  }, [filteredWorkstreams, searchQuery]);

  // Tracks rows the user has explicitly toggled while a search matched their
  // content, so manual clicks override the auto-expand-on-match behavior.
  const [manualRowOverrides, setManualRowOverrides] = useState<Set<number>>(new Set());

  // Clearing the search box always resets rows back to fully collapsed.
  useEffect(() => {
    if (!isSearching) {
      setManualRowOverrides(new Set());
      setExpandedIds(new Set());
    }
  }, [isSearching]);

  const isRowExpanded = (id: number) => {
    if (isSearching && contentMatchIds.has(id) && !manualRowOverrides.has(id)) return true;
    return expandedIds.has(id);
  };

  const toggleExpanded = (id: number) => {
    const currentlyExpanded = isRowExpanded(id);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (currentlyExpanded) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    if (isSearching) {
      setManualRowOverrides((prev) => new Set(prev).add(id));
    }
  };

  const renderExpandedDetails = (ws: WorkstreamDto) => {
    const findings = safeParseJsonArray<WorkstreamFinding>(ws.findingsJson);
    const redFlagsList = safeParseJsonArray<string>(ws.redFlags);
    const keyGapsList = safeParseJsonArray<string>(ws.keyGaps);

    return (
      <div className="p-4 space-y-4 bg-muted/30">
        {ws.summary && (
          <div>
            <h4 className="text-sm font-semibold mb-1">{t("summary")}</h4>
            <p className="text-sm text-muted-foreground">{highlightMatches(ws.summary, searchQuery)}</p>
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold mb-2">{t("findings")}</h4>
          {findings.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("noFindings")}</p>
          ) : (
            <div className="space-y-3">
              {findings.map((finding, index) => (
                <div key={index} className="rounded-lg border p-3 bg-background">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm">{highlightMatches(finding.title, searchQuery)}</span>
                    {finding.severity && getSeverityBadge(finding.severity)}
                  </div>
                  {finding.description && (
                    <p className="text-sm text-muted-foreground mb-1">{highlightMatches(finding.description, searchQuery)}</p>
                  )}
                  {finding.evidence && (
                    <p className="text-xs text-muted-foreground italic">
                      <span className="font-bold underline not-italic">{t("evidence")}:</span> {highlightMatches(finding.evidence, searchQuery)}
                    </p>
                  )}
                  {finding.recommendation && (
                    <p className="text-xs mt-1">
                      <span className="font-bold underline">{t("recommendation")}:</span> {highlightMatches(finding.recommendation, searchQuery)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {redFlagsList.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-1 text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle size={14} />
              {t("redFlags")}
            </h4>
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {redFlagsList.map((flag, index) => (
                <li key={index} className="text-muted-foreground">{highlightMatches(flag, searchQuery)}</li>
              ))}
            </ul>
          </div>
        )}

        {keyGapsList.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-1">{t("keyGaps")}</h4>
            <ul className="list-disc list-inside text-sm space-y-0.5">
              {keyGapsList.map((gap, index) => (
                <li key={index} className="text-muted-foreground">{highlightMatches(gap, searchQuery)}</li>
              ))}
            </ul>
          </div>
        )}

        {(ws.documentType || ws.confidence !== null) && (
          <div className="flex gap-4 text-xs text-muted-foreground pt-2 border-t">
            {ws.documentType && (
              <span>{t("documentType")}: {highlightMatches(ws.documentType, searchQuery)}</span>
            )}
            {ws.confidence !== null && (
              <span>{t("confidence")}: {Math.round(ws.confidence * 100)}%</span>
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex gap-3">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-[180px]" />
        </div>
        <div className="rounded-md border">
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("search")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder={t("status")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("allStatuses")}</SelectItem>
            <SelectItem value={String(WorkstreamStatus.Pending)}>{t("wsPending")}</SelectItem>
            <SelectItem value={String(WorkstreamStatus.Running)}>{t("wsRunning")}</SelectItem>
            <SelectItem value={String(WorkstreamStatus.Completed)}>{t("wsCompleted")}</SelectItem>
            <SelectItem value={String(WorkstreamStatus.Failed)}>{t("wsFailed")}</SelectItem>
          </SelectContent>
        </Select>
        {hasActiveFilters && (
          <Button variant="ghost" size="icon" onClick={clearFilters}>
            <X size={16} />
          </Button>
        )}
      </div>

      <div className="rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead className="w-[180px]">{t("workstream")}</TableHead>
              <TableHead className="w-[120px]">
                <Button variant="ghost" size="sm" className="h-8 p-0" onClick={() => handleSort("status")}>
                  {t("status")}
                  {getSortIcon("status")}
                </Button>
              </TableHead>
              <TableHead className="hidden sm:table-cell">{t("summary")}</TableHead>
              <TableHead className="w-[110px]">
                <Button variant="ghost" size="sm" className="h-8 p-0" onClick={() => handleSort("riskScore")}>
                  {t("riskScore")}
                  {getSortIcon("riskScore")}
                </Button>
              </TableHead>
              <TableHead className="w-[110px]">{t("confidence")}</TableHead>
              <TableHead className="w-[100px]">{t("redFlags")}</TableHead>
              <TableHead className="w-[100px]">{t("keyGaps")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  {t("noWorkstreams")}
                </TableCell>
              </TableRow>
            ) : (
              groups.map((group) => {
                const GroupIconComponent = AGENT_TYPE_ICONS[group.agentType];
                const groupExpanded = isGroupExpanded(group.agentType);

                return (
                  <Fragment key={group.agentType}>
                    <TableRow
                      className="cursor-pointer bg-muted/50 hover:bg-muted font-medium"
                      role="button"
                      tabIndex={0}
                      aria-expanded={groupExpanded}
                      onClick={() => toggleGroup(group.agentType)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleGroup(group.agentType);
                        }
                      }}
                    >
                      <TableCell className="w-8">
                        {groupExpanded
                          ? <ChevronDown size={16} className="text-muted-foreground" />
                          : <ChevronRight size={16} className="text-muted-foreground" />}
                      </TableCell>
                      <TableCell colSpan={7}>
                        <div className="flex items-center gap-2">
                          {GroupIconComponent && <GroupIconComponent size={18} />}
                          <span>{getAgentLabel(group.agentType)}</span>
                          <Badge variant="outline">{group.items.length}</Badge>
                          {group.redFlagsTotal > 0 && (
                            <Badge variant="destructive">
                              {group.redFlagsTotal} {t("redFlags")}
                            </Badge>
                          )}
                          {group.keyGapsTotal && group.keyGapsTotal > 0 && (
                            <Badge variant="secondary">
                              {group.keyGapsTotal} {t("keyGaps")}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {groupExpanded && group.items.map((ws) => {
                      const isExpanded = isRowExpanded(ws.id);
                      const redFlagsList = safeParseJsonArray<string>(ws.redFlags);
                      const keyGapsList = safeParseJsonArray<string>(ws.keyGaps);
                      const redFlagsCount = ws.redFlagsCount ?? redFlagsList.length;
                      const keyGapsCount = ws.keyGapsCount ?? keyGapsList.length;
                      const summaryText = ws.summary
                        ? ws.summary.length > 80
                          ? `${ws.summary.substring(0, 80)}…`
                          : ws.summary
                        : null;

                      return (
                        <Collapsible
                          key={ws.id}
                          open={isExpanded}
                          onOpenChange={() => toggleExpanded(ws.id)}
                          asChild
                        >
                          <>
                            <CollapsibleTrigger asChild>
                              <TableRow className="cursor-pointer hover:bg-muted/50">
                                <TableCell className="w-8" />
                                <TableCell>
                                  <div className="flex items-center gap-2 pl-4 text-sm text-muted-foreground">
                                    {isExpanded
                                      ? <ChevronDown size={14} />
                                      : <ChevronRight size={14} />}
                                    {t("workstream")} #{ws.id}
                                  </div>
                                </TableCell>
                                <TableCell>{getStatusBadge(ws.status)}</TableCell>
                                <TableCell className="hidden sm:table-cell">
                                  <span className="block truncate text-sm text-muted-foreground">
                                    {summaryText ?? "—"}
                                  </span>
                                </TableCell>
                                <TableCell>{getRiskScoreDisplay(ws.riskScore)}</TableCell>
                                <TableCell>{getConfidenceDisplay(ws.confidence)}</TableCell>
                                <TableCell>
                                  {redFlagsCount > 0 ? (
                                    <Badge variant="destructive">{redFlagsCount}</Badge>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {keyGapsCount > 0 ? (
                                    <Badge variant="secondary">{keyGapsCount}</Badge>
                                  ) : (
                                    <span className="text-muted-foreground">—</span>
                                  )}
                                </TableCell>
                              </TableRow>
                            </CollapsibleTrigger>
                            <CollapsibleContent asChild>
                              <tr>
                                <td colSpan={8}>
                                  {renderExpandedDetails(ws)}
                                </td>
                              </tr>
                            </CollapsibleContent>
                          </>
                        </Collapsible>
                      );
                    })}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default WorkstreamsTab;
